import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {atomButtonAlignment, atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import ActionsShareUrl, {MODAL_SHARE_CONTEXTS, shareUrlButtonTypes} from '@adv-mt/mt-actions-share-url'

import {trackingOrigins} from './config.js'

const NEW_LINE = '\n'

const ActionsAdShare = ({
  ad,
  alignment,
  extendedTrackingData = {},
  icon,
  shareText,
  size: buttonSize = atomButtonSizes.SMALL,
  type: buttonType = shareUrlButtonTypes.ACTION_BUTTON,
  trackingOrigin
}) => {
  const [sharedTrackingData, setSharedTrackingData] = useState(null)
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {SHARE} = config.get('COMMON')
  const {
    UTM_CAMPAIGN_QUERY_PARAM,
    UTM_CAMPAIGN_SHARE_ADS,
    UTM_MEDIUM_QUERY_PARAM,
    UTM_MEDIUM_SOCIAL_MEDIA,
    UTM_SOURCE_GENERAL,
    UTM_SOURCE_QUERY_PARAM
  } = SHARE

  const companyUrl = config.get('COMPANY_URL')
  const companyName = config.get('COMPANY_NAME')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')
  const {url: adUrl} = ad

  const getKmValue = km => {
    if (km === -1) return i18n.t('COMMON.OFFER_TYPE.KM0')
    if (km) return `${i18n.n(km)} ${i18n.t('COMMON.UNIT_KMS')}`
    return undefined
  }

  const kmValue = getKmValue(ad.km)

  const messageKey = ad.priceDrop ? 'COMMON.SHARE_AD.MESSAGE_WITH_PRICE_DROP' : 'COMMON.SHARE_AD.MESSAGE'
  const companyNameForShare = companyName.replaceAll('.', ' ')

  const defaultShareText =
    [
      i18n.t(messageKey, {companyName: companyNameForShare}),
      ad.title,
      ad.price ? `${i18n.t('COMMON.PRICE')}: ${i18n.c(ad.price)}` : undefined,
      kmValue ? `${i18n.t('COMMON.KMS')}: ${kmValue}` : undefined,
      ad.year ? `${i18n.t('COMMON.YEAR')}: ${ad.year}` : undefined
    ]
      .filter(Boolean)
      .join(NEW_LINE) + NEW_LINE

  const baseShareUrl = `${companyUrl}${adUrl}`

  const extendUrl = ({url, trackingData}) => {
    const urlObject = new URL(url)

    urlObject.searchParams.set(UTM_MEDIUM_QUERY_PARAM, UTM_MEDIUM_SOCIAL_MEDIA)
    urlObject.searchParams.set(UTM_SOURCE_QUERY_PARAM, UTM_SOURCE_GENERAL)
    urlObject.searchParams.set(UTM_CAMPAIGN_QUERY_PARAM, UTM_CAMPAIGN_SHARE_ADS)

    if (trackingData?.provinces)
      urlObject.searchParams.set('provincesTracking', encodeURIComponent(trackingData.provinces))

    return urlObject.toString()
  }

  const extendedBaseUrl = extendUrl({url: baseShareUrl, trackingData: extendedTrackingData})

  useEffect(() => {
    const getSharedTrackingData = async () => {
      const trackingData = await domain
        .get('get_event_tracking_data_use_case')
        .execute({ad, event: TRACKING_EVENT_NAMES.AD_SHARED, extendedTrackingData, origin: trackingOrigin})

      if (trackingData) {
        setSharedTrackingData(trackingData)
      }
    }

    getSharedTrackingData()
  }, []) // eslint-disable-line

  return (
    <ActionsShareUrl
      sharedTrackingData={sharedTrackingData}
      url={extendedBaseUrl}
      icon={icon}
      type={buttonType}
      trackingOrigin={trackingOrigin}
      trackingSharedEventName={TRACKING_EVENT_NAMES.AD_SHARED}
      trackingClickEventName={TRACKING_EVENT_NAMES.AD_SHARED_CLICKED}
      size={buttonSize}
      shareText={shareText ?? defaultShareText}
      alignment={alignment}
      shareContext={MODAL_SHARE_CONTEXTS.AD_DETAIL}
    />
  )
}

ActionsAdShare.displayName = 'ActionsAdShare'

ActionsAdShare.propTypes = {
  alignment: PropTypes.string,
  ad: PropTypes.object,
  extendedTrackingData: PropTypes.object,
  icon: PropTypes.node,
  shareText: PropTypes.string,
  size: PropTypes.oneOf(Object.values(atomButtonSizes)),
  type: PropTypes.oneOf(shareUrlButtonTypes),
  trackingOrigin: PropTypes.oneOf(Object.values(trackingOrigins))
}

export {
  shareUrlButtonTypes as actionAdShareTypes,
  atomButtonSizes as actionAdShareButtonSizes,
  atomButtonAlignment as actionAdShareButtonAlignment,
  trackingOrigins as actionAdShareTrackingOrigins
}

export default ActionsAdShare
